import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router';
import { SiteRegistrationForm } from '../types';
import { ExternalAuthorisation, User } from '../../../types';
import { getCookie, roundNumber } from '../../../utils';
import AddressValidator from './registration_steps/AddressValidator';
import { MapMarker } from '../../map/types';
import { MapPinIcon, EnvelopeIcon, CheckIcon, BuildingOfficeIcon, LinkIcon } from '@heroicons/react/24/outline';
import GeneralStep from './registration_steps/GeneralStep';
import ReviewStep from './registration_steps/ReviewStep';
import AuthStep from './registration_steps/AuthStep';
import ConnectStep from './registration_steps/ConnectStep';
import FormFieldSet from './FormFieldSet';
import FloatingLabel from './FloatingLabel';
import FormSelector from './FormSelector';
import { Invitation } from '../types';

const TAB_LABELS_FULL = ['General', 'Location', 'Authorisation', 'Connection', 'Review'];
const TAB_LABELS_PREFILLED = ['General', 'Authorisation', 'Connection', 'Review'];

const RegistrationPage: React.FC = () => {
    const { user } = useOutletContext<{ user: User }>();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const invitation: Invitation | null = (window as any).invitation;
    
    // Determine if sites are pre-filled (invitation has sites array)
    const hasPrefilled = invitation?.sites && invitation.sites.length > 0;
    const TAB_LABELS = hasPrefilled ? TAB_LABELS_PREFILLED : TAB_LABELS_FULL;
    
    const [formData, setFormData] = useState<SiteRegistrationForm>({
        name: '',
        address: '',
        project: projectId || invitation?.project.id.toString() || '',
        manufacturer: '',
        latitude: null,
        longitude: null,
        health_status: 'normal',
        external_access_token: '',
        external_site_id: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [marker, setMarker] = useState<MapMarker | null>(null);
    const [authorised, setAuthorised] = useState(false);
    const [connected, setConnected] = useState(false);
    const [externalAuthorisations, setExternalAuthorisations] = useState<ExternalAuthorisation[]>([]);
    const [selectedExternalAuthorisation, setSelectedExternalAuthorisation] = useState<ExternalAuthorisation | null>(null);
    const [newExternalAuthorisation, setNewExternalAuthorisation] = useState<ExternalAuthorisation | null>(null);
    const [siteMatches, setSiteMatches] = useState<{ [siteId: number]: string }>({});

    const isFormComplete: boolean = hasPrefilled
        ? !!formData.manufacturer && authorised && connected
        : !!formData.name && !!formData.manufacturer && !!formData.address && !!formData.latitude && !!formData.longitude && authorised && connected;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                navigate(`/dashboard/project/${projectId}`);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [formData]);

    useEffect(() => {
        // For invitations, fetch external authorisations from the installer
        if (invitation?.installer) {
            fetch(`/api/installers/${invitation.installer.id}/external_authorisations/`)
                .then(response => response.json())
                .then(data => {
                    if (formData.manufacturer) {
                        setExternalAuthorisations(data.filter((authorisation: ExternalAuthorisation) => authorisation.manufacturer === formData.manufacturer));
                    } else {
                        setExternalAuthorisations(data);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch external authorisations:', error);
                    setExternalAuthorisations([]);
                });
        } else {
            // For non-invitation flows, clear external authorisations
            setExternalAuthorisations([]);
        }
    }, [invitation, formData.manufacturer, newExternalAuthorisation]);

    const handlePlaceSelect = (place: google.maps.places.Place) => {
        setFormData(prev => ({
            ...prev,
            address: place.formattedAddress!,
            latitude: roundNumber(place.location!.lat(), 6),
            longitude: roundNumber(place.location!.lng(), 6)
        }));
        setMarker({
            id: Number(place.id),
            latitude: place.location!.lat(),
            longitude: place.location!.lng(),
            title: place.formattedAddress!,
            active: false,
            health_status: 'normal'
        });
    }

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormComplete) {
            setCurrentTab(TAB_LABELS.length - 1);
            return;
        }
        setIsSubmitting(true);
        try {
            if (invitation) {
                // For invitation flow with multiple sites
                const externalAuthorisation = newExternalAuthorisation || selectedExternalAuthorisation;
                if (!externalAuthorisation) {
                    throw new Error('External authorisation is required');
                }
                
                const response = await fetch(`/site-connect-invitation/${invitation.token}/finished/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCookie('csrftoken') || '',
                    },
                    body: JSON.stringify({
                        'site_matches': siteMatches,
                        'external_authorisation_id': externalAuthorisation.id
                    })
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.error || 'Failed to complete site registration');
                }
                
                if (data.success) {
                    invitation.finished = true;
                    let message = `Successfully registered ${data.processed_sites.length} site(s)!`;
                    if (data.skipped_sites && data.skipped_sites.length > 0) {
                        message += `\n\n${data.skipped_sites.length} site(s) were skipped (not matched).`;
                    }
                    alert(message);
                    navigate(`/dashboard/project/${invitation.project.id}`);
                    navigate(0);
                } else if (data.errors && data.errors.length > 0) {
                    const errorMessages = data.errors.map((e: any) => `${e.site_name}: ${e.error}`).join('\n');
                    let message = `Some sites failed to register:\n${errorMessages}`;
                    if (data.processed_sites && data.processed_sites.length > 0) {
                        message = `Successfully registered ${data.processed_sites.length} site(s).\n\n` + message;
                    }
                    alert(message);
                    if (data.processed_sites && data.processed_sites.length > 0) {
                        navigate(`/dashboard/project/${invitation.project.id}`);
                        navigate(0);
                    }
                }
            } else {
                // For single site creation flow
                const response = await fetch(`/api/sites/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCookie('csrftoken') || '',
                    },
                    body: JSON.stringify({
                        'data': formData,
                        'external_authorisation': newExternalAuthorisation || selectedExternalAuthorisation
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to create site');
                }
                
                navigate(`/dashboard/project/${projectId}`);
                navigate(0);
            }
        } catch (error) {
            console.error(error);
            alert(error instanceof Error ? error.message : 'Failed to create site. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getTabIcon = (label: string): React.ReactNode => {
        if (label === 'Location') {
            return <MapPinIcon className="w-4 h-4" />;
        } else if (label === 'Authorisation') {
            return <EnvelopeIcon className="w-4 h-4" />;
        } else if (label === 'Connection') {
            return <LinkIcon className="w-4 h-4" />;
        } else if (label === 'Review') {
            return <CheckIcon className="w-4 h-4" />;
        } else if (label === 'General') {
            return <BuildingOfficeIcon className="w-4 h-4" />;
        }
        return null;
    }

    const handleTabClick = (idx: number) => {
        if (currentTab >= idx || (idx === currentTab + 1 && isNextOK())) {
            setCurrentTab(idx);
        }
    };

    const isNextOK = () => {
        const tabLabel = TAB_LABELS[currentTab];

        if (tabLabel === 'General') {
            return hasPrefilled ? !!formData.manufacturer : !!formData.name;
        } else if (tabLabel === 'Location') {
            return formData.address && formData.latitude && formData.longitude;
        } else if (tabLabel === 'Authorisation') {
            if (externalAuthorisations.length > 0 && selectedExternalAuthorisation) {
                return true;
            } else {
                return authorised;
            }
        } else if (tabLabel === 'Connection') {
            return connected;
        }
        return true;
    }

    const handleNext = () => {
        if (isNextOK()) {
            setCurrentTab((prev) => Math.min(prev + 1, TAB_LABELS.length - 1));
        }
    };

    const handleBack = () => {
        setCurrentTab((prev) => Math.max(prev - 1, 0));
    };

    const resetPage = () => {
        setFormData({
            name: '',
            manufacturer: '',
            address: '',
            project: projectId!,
            latitude: null,
            longitude: null,
            health_status: 'normal',
            external_access_token: '',
            external_site_id: ''
        });
        setCurrentTab(0);
        setAuthorised(false);
        setConnected(false);
        setSelectedExternalAuthorisation(null);
        setNewExternalAuthorisation(null);
        setMarker(null);
    }

    return (
        <div className="p-6 flex justify-center h-full">
            <div className={`max-w-2xl w-full ${invitation && invitation.finished && 'flex flex-col justify-center items-center'}`}>
                { invitation && invitation.finished
                    ? <p className='h-auto w-auto'>Invitation has successfully been completed!</p>
                    : <>
                        <div className="mb-6 text-center">
                            <h1 className="text-xl font-bold">Site Registration</h1>
                            {invitation
                                ? <div className="mb-6 flex flex-col gap-1">
                                    <p>Register {hasPrefilled ? `${invitation.sites.length} site${invitation.sites.length > 1 ? 's' : ''}` : 'a new site'} for the project {invitation.project.name} in {invitation.project.locality}</p>
                                    <p>This invitation will expire at {invitation.expires_at.toLocaleString()}</p>
                                </div>
                                : <p className="mb-6">Register a new site for this project</p>
                            }
                            <ul className="steps">
                                {TAB_LABELS.map((label, idx) => (
                                    <li key={label} className={`step${currentTab >= idx ? ' step-neutral' : ''}`}>
                                        <span className="step-icon">{getTabIcon(label)}</span>
                                        <a className={`link text-sm hover:text-neutral no-underline ${currentTab < idx && 'text-base-400'}`} onClick={() => handleTabClick(idx)} style={{ cursor: currentTab >= idx || (idx === currentTab + 1 && isNextOK()) ? 'pointer' : 'not-allowed' }}>{label}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {TAB_LABELS[currentTab] === 'General' && (
                                hasPrefilled && invitation?.sites ? (
                                    <FormFieldSet legend="Sites to Register">
                                        <div className="space-y-2">
                                            <p className="text-sm text-gray-600 mb-4">
                                                The following sites will be registered with the selected manufacturer and connection details:
                                            </p>
                                            <div className="overflow-x-auto">
                                                <table className="table table-sm w-full">
                                                    <thead>
                                                        <tr>
                                                            <th>Site Name</th>
                                                            <th>Address</th>
                                                            <th>Location</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invitation.sites.map((site) => (
                                                            <tr key={site.id}>
                                                                <td className="font-medium">{site.name}</td>
                                                                <td>{site.address}</td>
                                                                <td className="text-xs text-gray-500">
                                                                    {site.latitude && site.longitude
                                                                        ? `${Number(site.latitude).toFixed(4)}, ${Number(site.longitude).toFixed(4)}`
                                                                        : ''}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <FloatingLabel className="mt-4">
                                            <span className={`${!formData.manufacturer && 'text-error'}`}>Manufacturer</span>
                                            <FormSelector
                                                label="Manufacturer"
                                                options={["Victron"]} // TODO: add other manufacturers
                                                optionToItemCallback={(option) => <option key={option} value={option}>{option}</option>}
                                                name="manufacturer"
                                                value={formData.manufacturer}
                                                onChange={handleInputChange}
                                                required={true}
                                            />
                                        </FloatingLabel>
                                    </FormFieldSet>
                                ) : (
                                    <GeneralStep formData={formData} handleInputChange={handleInputChange} prefilledSiteName={null} />
                                )
                            )}

                            {TAB_LABELS[currentTab] === 'Location' && !hasPrefilled && (
                                <AddressValidator
                                    handleAddressChange={handleAddressChange}
                                    marker={marker}
                                    handlePlaceSelect={handlePlaceSelect}
                                />
                            )}

                            {TAB_LABELS[currentTab] === 'Authorisation' && (
                                <AuthStep formData={formData} handleInputChange={handleInputChange} authorised={authorised} setAuthorised={setAuthorised} externalAuthorisations={externalAuthorisations} selectedExternalAuthorisation={selectedExternalAuthorisation} setSelectedExternalAuthorisation={setSelectedExternalAuthorisation} newExternalAuthorisation={newExternalAuthorisation} setNewExternalAuthorisation={setNewExternalAuthorisation} invitation={invitation} />
                            )}

                            {TAB_LABELS[currentTab] === 'Connection' && (
                                <ConnectStep formData={formData} handleInputChange={handleInputChange} authorised={authorised} connected={connected} setConnected={setConnected} selectedExternalAuthorisation={selectedExternalAuthorisation} invitation={invitation} siteMatches={siteMatches} setSiteMatches={setSiteMatches} />
                            )}

                            {TAB_LABELS[currentTab] === 'Review' && (
                                <ReviewStep formData={formData} isFormComplete={isFormComplete} marker={marker} invitation={invitation} selectedExternalAuthorisation={selectedExternalAuthorisation || newExternalAuthorisation} siteMatches={siteMatches} />
                            )}

                            {/* Navigation Buttons */}
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (invitation) {
                                            resetPage();
                                        } else {
                                            navigate(`/dashboard/project/${projectId}`);
                                        }
                                    }}
                                    className="btn btn-ghost flex-1"
                                >
                                    {invitation ? 'Reset' : 'Cancel'}
                                </button>
                                {currentTab > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline flex-2"
                                        onClick={handleBack}
                                    >
                                        Previous
                                    </button>
                                )}
                                {currentTab < TAB_LABELS.length - 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-neutral flex-2"
                                        onClick={handleNext}
                                        disabled={!isNextOK()}
                                    >
                                        Next
                                    </button>
                                )}
                                {currentTab === TAB_LABELS.length - 1 && (
                                    <div className={`flex-2 ${!isFormComplete ? 'tooltip tooltip-warning tooltip-top' : ''}`} data-tip="Form is incomplete">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting || !isFormComplete}
                                            className="btn btn-neutral w-full"
                                        >
                                            {isSubmitting && <span className="loading loading-spinner"></span>}
                                            {isSubmitting ? 'Creating...' : 'Create Site'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </form></>
                }
            </div>
        </div>
    );
};

export default RegistrationPage;